/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import {
    Alert,
    Box,
    ContentBlock,
    DividerNew as Divider,
    FormValidationAlert,
    Heading,
    NeedToKnow,
    NotificationContainer,
    ProgressIndicator,
    Spacer,
    Stack,
    useFlags,
} from 'sarsaparilla';

import { NeedToKnowList } from 'shared-ui/components/NeedToKnowList/NeedToKnowList';
import {
    OrderDetailsLayout,
    OrderDetailsSection,
    OrderDetailsTimerNotice,
} from 'shared-ui/components/OrderDetailsLayout';
import { gaReportingCartReservationsForStepper } from 'shared-ui/utils/gaCommon';

import { ModifyModal } from '../../containers/OrderDetailsPage/ModifyModal';
import OrderSummary from './OrderSummary/OrderSummary';
import DetailSummary from './OrderSummary/DetailSummary';
import Details from './Details';
import TicketHolderDetails from './TicketHolderDetails';
import VehicleDetails from './VehicleDetails';
import Passes from './Passes';
import AddPass from './AddPass';
import SitePass from './SitePass/SitePass';
import { SitePassRecaptchaModal } from './SitePassRecaptchaModal';

export function OrderDetailsPage({
    imageUrl,
    acceptsPasses,
    progressBarActiveStep = 0,
    orderDetails,
    onModifyClick,
    onRemoveClick,
    isLoading,
    isNeedToKnowChecked,
    isSitePassInfoChecked,
    onNeedToKnowChange,
    onSitePassInfoChange,
    onProceedToCartClick,
    onContinueShopping,
    isChallengeModalOpen,
    recaptchaCheckboxProps,
    isV2Invalid,
    onChallengeContinue,
    proceedToCartButtonText,
    isSpinning,
    validationErrors,
    addValidationRef,
    apiError,
    isExistingReservation,
    hasPendingModification,
    tour,
    reservation,
    cartReservations,
    validDays,
    showSitePasses,
    availableSitePass,
    userMustBuyAPassOnPurchase,
    sitePassReservationsExist,
    newSitePassIntegrationEnabled,
    sitePassNotRequiredForDate,
    isFacilitySitePassMapped,
    showDigitalPassDiscounts,
    showBuyNow,
    reservationId,
}) {
    const { timedEntryPassesV2: acceptsPassesV2 } = useFlags();
    const orderDetailsPageRefs = useRef({ sitePassRefId: uuidv4() });
    const startTime = moment(orderDetails?.reservation?.tour_time, 'HHmm');
    const endTime = startTime?.clone()?.add(orderDetails?.tour_duration, 'minutes');

    const endDate = useMemo(() => {
        if (!!validDays && validDays > 1) {
            return moment(orderDetails?.reservation?.tour_date)
                ?.add(validDays - 1, 'day')
                ?.format('ll');
        }

        return '';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderDetails?.reservation?.tour_date, validDays]);

    const formatDate = useCallback((date, initialFormat = null, dateFormat = 'll') => {
        if (!initialFormat) return moment(date)?.format(dateFormat);

        return moment(date, initialFormat)?.format(dateFormat);
    }, []);

    const date = useMemo(() => {
        return formatDate(orderDetails?.reservation.tour_date);
    }, [formatDate, orderDetails?.reservation.tour_date]);

    const formattedStartTime = useMemo(() => {
        return formatDate(startTime, null, 'LT');
    }, [formatDate, startTime]);

    const formattedEndTime = useMemo(() => {
        return formatDate(endTime, null, 'LT');
    }, [endTime, formatDate]);

    const renderPasses = () => {
        if (!acceptsPasses) return null;

        const guestTypesSupportPasses = Object.keys(
            isEmpty(reservation.pending_modifications)
                ? reservation.ticket_count_by_guest_type_id
                : reservation.pending_modifications.ticket_count_by_guest_type_id
        ).some((guestType) => !isEmpty(tour.passes_by_guest_type_id[guestType]));

        if (!guestTypesSupportPasses) return null;

        return (
            <>
                <Divider />
                {acceptsPassesV2 ? (
                    <Passes
                        guestTypeNameByGuestTypeId={
                            reservation.facility_guest_type_name_by_guest_type_id
                        }
                        ticketCountByGuestTypeId={
                            isEmpty(reservation.pending_modifications)
                                ? reservation.ticket_count_by_guest_type_id
                                : reservation.pending_modifications
                                      .ticket_count_by_guest_type_id
                        }
                        passesFromTour={tour.passes_by_guest_type_id}
                        passesFromReservation={reservation.passes_by_guest_type_id}
                        addValidationRef={addValidationRef}
                        reservation={reservation}
                        showDigitalPassDiscounts={showDigitalPassDiscounts}
                    />
                ) : (
                    <AddPass
                        reservation={orderDetails?.reservation || {}}
                        addValidationRef={addValidationRef}
                    />
                )}
            </>
        );
    };

    const checkboxText = useMemo(() => {
        if (sitePassNotRequiredForDate) {
            return 'A site pass is not required for this day but a timed entry reservation is still a requirement.';
        }

        // FACILITY_SITEPASS facilities skip the "already have a pass" ownership message
        if (
            newSitePassIntegrationEnabled &&
            !userMustBuyAPassOnPurchase &&
            !isFacilitySitePassMapped
        ) {
            return 'You already have a Site Pass for this date, you do not need another Site Pass.';
        }

        return 'I understand a pass is also required at this location.';
    }, [
        sitePassNotRequiredForDate,
        userMustBuyAPassOnPurchase,
        newSitePassIntegrationEnabled,
        isFacilitySitePassMapped,
    ]);

    const sitePassErrorText = useMemo(() => {
        if (
            isSitePassInfoChecked &&
            userMustBuyAPassOnPurchase &&
            !sitePassReservationsExist
        ) {
            return 'A Site Pass is required for this location. Please add a Site Pass to continue.';
        }

        return 'Please read and agree to the important Site Pass information.';
    }, [isSitePassInfoChecked, userMustBuyAPassOnPurchase, sitePassReservationsExist]);

    const summary = (
        <OrderSummary
            isLoading={isLoading}
            heading={orderDetails.tour_name}
            imageUrl={imageUrl}
            isWaitingForServerResponse={isSpinning}
            parentName={orderDetails.facility_name}
            isExistingReservation={isExistingReservation}
            hasPendingModification={hasPendingModification}
            onProceedToCartClick={onProceedToCartClick}
            proceedToCartButtonText={proceedToCartButtonText}
            onRemoveClick={onRemoveClick}
            onContinueShopping={onContinueShopping}
            secondaryButtonIsDisabled={
                !isNeedToKnowChecked || (showSitePasses && !isSitePassInfoChecked)
            }
            showSitePasses={showSitePasses}
            validDays={validDays}
            date={date}
            endDate={endDate}
            startTime={formattedStartTime}
            endTime={formattedEndTime}
            isDrawerWrapFullWidth
            showBuyNow={showBuyNow}
            reservationId={reservationId}
        >
            <DetailSummary
                isExistingReservation={isExistingReservation}
                showSitePasses={showSitePasses}
                validDays={validDays}
                date={date}
                endDate={endDate}
                startTime={formattedStartTime}
                endTime={formattedEndTime}
            />
        </OrderSummary>
    );

    if (
        (!isLoading && !orderDetails?.reservation?.facility_id) ||
        orderDetails?.reservation?.status === 'DISCARDED'
    ) {
        return (
            <ContentBlock style={{ height: '50vh' }}>
                <Box alignX="center" alignY="center" style={{ height: '100%' }}>
                    <Alert type="error" heading="Can not find reservation">
                        This reservation has expired or does not exist.
                    </Alert>
                </Box>
            </ContentBlock>
        );
    }

    const hasModifyUrl = window.location.pathname.includes('modify');
    const progressIndicatorSteps = [
        'Order Details',
        'Review Cart',
        'Payment',
        'Confirmation',
    ];

    return (
        <main className="te-order-details-page">
            <NotificationContainer className="mb-0" />

            {!isLoading && <ModifyModal />}

            <SitePassRecaptchaModal
                isOpen={isChallengeModalOpen}
                recaptchaCheckboxProps={recaptchaCheckboxProps}
                isV2Invalid={isV2Invalid}
                onContinue={onChallengeContinue}
            />

            <Box background="gray-2" paddingY="sm">
                <ProgressIndicator
                    eventToReportToGA={gaReportingCartReservationsForStepper(
                        cartReservations,
                        progressIndicatorSteps,
                        progressBarActiveStep
                    )}
                    shouldReportToGA
                    steps={progressIndicatorSteps}
                    activeStep={progressBarActiveStep}
                />
            </Box>

            <Spacer size="xl" />

            <ContentBlock>
                <OrderDetailsLayout summaryElement={summary}>
                    <Heading headingLevel={1} appearance="h3">
                        Order Details
                    </Heading>
                    {!hasModifyUrl && !isExistingReservation && (
                        <OrderDetailsTimerNotice />
                    )}

                    <FormValidationAlert errors={validationErrors} />
                    {apiError && (
                        <Alert type="error" heading="Error updating the reservation">
                            {apiError}
                        </Alert>
                    )}

                    <OrderDetailsSection
                        heading={orderDetails.tour_name}
                        onRemoveClick={isExistingReservation ? null : onRemoveClick}
                        isLoading={isLoading}
                        modifyText="Change Date/Time"
                    >
                        <Stack space="xl">
                            <Details
                                validDays={validDays}
                                onModifyClick={onModifyClick}
                                date={date}
                                endDate={endDate}
                                startTime={formattedStartTime}
                                endTime={formattedEndTime}
                            />

                            <Divider />

                            <TicketHolderDetails
                                addValidationRef={addValidationRef}
                                isExistingReservation={isExistingReservation}
                            />
                            {!!tour?.collect_license_plate_information && (
                                <>
                                    <Divider />
                                    <VehicleDetails addValidationRef={addValidationRef} />
                                </>
                            )}

                            {renderPasses()}
                        </Stack>
                    </OrderDetailsSection>
                    <SitePass
                        ref={addValidationRef(
                            orderDetailsPageRefs?.current?.sitePassRefId
                        )}
                        facilityId={orderDetails?.facility_id}
                        isChecked={isSitePassInfoChecked}
                        visible={showSitePasses}
                        handleChange={onSitePassInfoChange}
                        defaultStartDate={orderDetails?.reservation.tour_date}
                        availableSitePass={availableSitePass}
                        checkboxText={checkboxText}
                        errorText={sitePassErrorText}
                        newSitePassIntegrationEnabled={newSitePassIntegrationEnabled}
                        userMustBuyAPass={userMustBuyAPassOnPurchase}
                        sitePassNotRequiredForDate={sitePassNotRequiredForDate}
                    />
                    <NeedToKnow
                        siteName={orderDetails.facility_name}
                        isChecked={isNeedToKnowChecked}
                        onChange={onNeedToKnowChange}
                        ref={addValidationRef(20)}
                    >
                        <div className="rec-information-head">Facility Information</div>
                        <NeedToKnowList
                            isLoading={isLoading}
                            listItems={orderDetails.tour_notes}
                        />
                    </NeedToKnow>
                </OrderDetailsLayout>
            </ContentBlock>
        </main>
    );
}

OrderDetailsPage.propTypes = {
    progressBarActiveStep: PropTypes.number,
    orderDetails: PropTypes.object,
    onModifyClick: PropTypes.func,
    onRemoveClick: PropTypes.func,
    isLoading: PropTypes.bool,
    isSpinning: PropTypes.bool,
    imageUrl: PropTypes.string,
    isNeedToKnowChecked: PropTypes.bool,
    isSitePassInfoChecked: PropTypes.bool,
    onNeedToKnowChange: PropTypes.func,
    onSitePassInfoChange: PropTypes.func,
    onProceedToCartClick: PropTypes.func,
    onContinueShopping: PropTypes.func,
    isChallengeModalOpen: PropTypes.bool,
    recaptchaCheckboxProps: PropTypes.object,
    isV2Invalid: PropTypes.bool,
    onChallengeContinue: PropTypes.func,
    proceedToCartButtonText: PropTypes.string,
    apiError: PropTypes.node,
    validationErrors: PropTypes.object,
    addValidationRef: PropTypes.func,
    isExistingReservation: PropTypes.bool,
    hasPendingModification: PropTypes.bool,
    acceptsPasses: PropTypes.bool,
    tour: PropTypes.object,
    reservation: PropTypes.object,
    cartReservations: PropTypes.arrayOf(PropTypes.object),
    validDays: PropTypes.number,
    showSitePasses: PropTypes.bool,
    availableSitePass: PropTypes.string,
    userMustBuyAPassOnPurchase: PropTypes.bool,
    sitePassReservationsExist: PropTypes.bool,
    newSitePassIntegrationEnabled: PropTypes.bool,
    sitePassNotRequiredForDate: PropTypes.bool,
    isFacilitySitePassMapped: PropTypes.bool,
    showBuyNow: PropTypes.bool,
    reservationId: PropTypes.string,
};

// cSpell:ignore uuidv
